import React from 'react'
import { UsePaginationProps, usePagination } from 'react-instantsearch'

import cn from 'classnames'

import { LightArrowLeft, LightArrowRight } from 'components/icon/light'
import Link from 'components/link'

import css from './pagination.module.scss'

function CustomPagination(props: UsePaginationProps) {
  const { pages, currentRefinement, nbPages, nbHits, isFirstPage, isLastPage, refine, createURL } = usePagination(props)
  const firstPageIndex = 0
  const previousPageIndex = currentRefinement - 1
  const nextPageIndex = currentRefinement + 1
  const lastPageIndex = nbPages - 1

  const isFirstPageExist = pages.find((page) => page === firstPageIndex) === 0
  const isLastPageExist = pages.find((page) => page === lastPageIndex)

  const hasOnePageOnly = nbPages === 1

  if (nbHits === 0) return null

  return (
    <ul className={css.container}>
      {!hasOnePageOnly && (
        <PaginationItem
          isDisabled={isFirstPage}
          href={createURL(previousPageIndex)}
          onClick={() => refine(previousPageIndex)}
          label="previous page"
        >
          <LightArrowLeft />
        </PaginationItem>
      )}
      {!isFirstPageExist && (
        <>
          <PaginationItem
            isDisabled={isFirstPage}
            isSelected={currentRefinement === firstPageIndex}
            href={createURL(firstPageIndex)}
            onClick={() => refine(firstPageIndex)}
            label={'First page'}
          >
            {firstPageIndex + 1}
          </PaginationItem>
          <PaginationItem isDeclaration={true}>...</PaginationItem>
        </>
      )}

      {pages.map((page) => {
        if (page === currentRefinement || page === currentRefinement - 1 || page === currentRefinement + 1) {
          const label = page + 1
          return (
            <PaginationItem
              key={page}
              isDisabled={false}
              isSelected={currentRefinement === page}
              aria-label={`Page ${label}`}
              href={createURL(page)}
              onClick={() => refine(page)}
              label={`page ${label}}`}
              classNameWrapper={css['pageItem--mobile']}
            >
              {label}
            </PaginationItem>
          )
        }
        return null
      })}

      {pages.map((page) => {
        const label = page + 1
        if (!isFirstPageExist && page === firstPageIndex) return null
        if (!isLastPageExist && page === lastPageIndex) return null

        return (
          <PaginationItem
            key={page}
            isDisabled={false}
            isSelected={currentRefinement === page}
            aria-label={`Page ${label}`}
            href={createURL(page)}
            onClick={() => refine(page)}
            label={`page ${label}}`}
            classNameWrapper={css['pageItem--desktop']}
          >
            {label}
          </PaginationItem>
        )
      })}
      {!isLastPageExist && (
        <>
          {!hasOnePageOnly && (
            <PaginationItem isDeclaration={true} classNameWrapper={css['pageItem--desktop']}>
              ...
            </PaginationItem>
          )}
          <PaginationItem
            isSelected={currentRefinement === lastPageIndex || hasOnePageOnly}
            isDisabled={isLastPage}
            href={createURL(lastPageIndex)}
            label={`last page`}
            onClick={() => refine(lastPageIndex)}
            classNameWrapper={css['pageItem--desktop']}
          >
            {lastPageIndex + 1}
          </PaginationItem>
        </>
      )}

      {!hasOnePageOnly && (
        <PaginationItem
          label="next page"
          isDisabled={isLastPage}
          href={createURL(nextPageIndex)}
          onClick={() => refine(nextPageIndex)}
        >
          <LightArrowRight />
        </PaginationItem>
      )}
    </ul>
  )
}

type PaginationItemProps = Omit<React.ComponentProps<'a'>, 'onClick'> & {
  onClick?: NonNullable<React.ComponentProps<'a'>['onClick']>
  isDisabled?: boolean
  isSelected?: boolean
  isDeclaration?: boolean
  label?: string
  classNameWrapper?: string
}

function PaginationItem({
  isSelected = false,
  isDisabled = false,
  isDeclaration,
  href,
  onClick = () => null,
  label = '',
  children,
  classNameWrapper,
  ...props
}: PaginationItemProps) {
  return (
    <li
      className={cn([
        css.pageItem,
        isSelected && css.selectedItem,
        isDisabled && css.disabledItem,
        isDeclaration && css.declarationItem,
        classNameWrapper,
      ])}
      aria-label={label}
    >
      {isSelected || isDisabled || isDeclaration ? (
        <span {...props} className={css.link}>
          {children}
        </span>
      ) : (
        <Link
          href={href}
          onClick={(event) => {
            if (isModifierClick(event)) {
              return
            }

            event.preventDefault()
            window.scrollTo(0, 0)
            onClick(event)
          }}
          className={css.link}
          {...props}
          target="_self"
        >
          {children}
        </Link>
      )}
    </li>
  )
}

function isModifierClick(event: React.MouseEvent) {
  const isMiddleClick = event.button === 1

  return Boolean(isMiddleClick || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
}

export default CustomPagination
